/** App root: QueryClient, BrowserRouter, auth-gated routes */

import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import { useAuth } from './hooks/use-auth'
import { useAppStore } from './stores/app-store'
import { AppShell } from './components/hybrid'
import { CommandPalette } from './components/command-palette/command-palette'
import { LoginPage } from './routes/login'
import { HomePage } from './routes/home'
import { DocsPage } from './routes/docs'
import { ProfilePage } from './routes/profile'
import { SettingsPage } from './routes/settings'
import { ApiDocsPage } from './routes/api-docs'
import { CliDocsPage } from './routes/cli-docs'
import { SearchAnalyticsPage } from './routes/search-analytics'
import { SearchPage } from './routes/search'
import { GraphPage } from './routes/graph'
import { ShareView } from './routes/share-view'
import { PublishedView } from './routes/published-view'
import { StoragePage } from './routes/storage'
import { HostedSitesPage } from './routes/hosted-sites'
import { BillingPage } from './routes/billing'
import { SecretsPage } from './routes/secrets'
import { AgentsPage } from './routes/agents'
import { DocumentAccessRoute } from './routes/document-access-route'
import { getWebFeatureProfile } from './lib/feature-flags'
import { roleHasPermission } from './lib/role-permissions'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
})

export function ThemeSync() {
  const theme = useAppStore((state) => state.theme)

  useEffect(() => {
    const root = document.documentElement
    root.classList.toggle('dark', theme === 'dark')
    root.classList.toggle('light', theme === 'light')
    root.setAttribute('data-theme', theme)
  }, [theme])

  return null
}

/** Wraps a route that requires authentication */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-0">
        <div className="flex flex-col items-center gap-3">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-neutral-800 border-t-brand-500" />
          <span className="text-xs text-neutral-600">Loading...</span>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return (
    <>
      {children}
      <CommandPalette />
    </>
  )
}

function AuthenticatedPage({ children }: { children: React.ReactNode }) {
  return (
    <RequireAuth>
      <AppShell>{children}</AppShell>
    </RequireAuth>
  )
}

function RequireRolePermission({
  permission,
  children,
}: {
  permission: string
  children: React.ReactNode
}) {
  const { role } = useAuth()
  return roleHasPermission(role, permission) ? children : <Navigate to="/" replace />
}

export function AppRoutes() {
  const profile = getWebFeatureProfile()
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/share/:token" element={<ShareView />} />
      <Route path="/pub/:slug" element={<PublishedView />} />
      <Route
        path="/profile"
        element={
          <AuthenticatedPage>
            <ProfilePage />
          </AuthenticatedPage>
        }
      />
      <Route
        path="/settings"
        element={
          <AuthenticatedPage>
            <SettingsPage />
          </AuthenticatedPage>
        }
      />
      <Route
        path="/docs/api"
        element={
          <AuthenticatedPage>
            <ApiDocsPage />
          </AuthenticatedPage>
        }
      />
      <Route
        path="/docs/cli"
        element={
          <AuthenticatedPage>
            <CliDocsPage />
          </AuthenticatedPage>
        }
      />
      <Route
        path="/search"
        element={
          <AuthenticatedPage>
            <SearchPage />
          </AuthenticatedPage>
        }
      />
      <Route
        path="/settings/search-analytics"
        element={
          <AuthenticatedPage>
            <RequireRolePermission permission="tenant:manage">
              <SearchAnalyticsPage />
            </RequireRolePermission>
          </AuthenticatedPage>
        }
      />
      <Route
        path="/graph"
        element={
          <AuthenticatedPage>
            <GraphPage />
          </AuthenticatedPage>
        }
      />
      {profile.storageSpace && (
        <>
          <Route
            path="/storage"
            element={
              <AuthenticatedPage>
                <RequireRolePermission permission="storage:read">
                  <StoragePage />
                </RequireRolePermission>
              </AuthenticatedPage>
            }
          />
          <Route
            path="/storage/:folderId"
            element={
              <AuthenticatedPage>
                <RequireRolePermission permission="storage:read">
                  <StoragePage />
                </RequireRolePermission>
              </AuthenticatedPage>
            }
          />
        </>
      )}
      <Route
        path="/sites"
        element={
          <AuthenticatedPage>
            <HostedSitesPage />
          </AuthenticatedPage>
        }
      />
      <Route
        path="/secrets"
        element={
          <AuthenticatedPage>
            <SecretsPage />
          </AuthenticatedPage>
        }
      />
      <Route
        path="/billing"
        element={
          <AuthenticatedPage>
            <BillingPage />
          </AuthenticatedPage>
        }
      />
      <Route
        path="/agents"
        element={
          <AuthenticatedPage>
            <AgentsPage />
          </AuthenticatedPage>
        }
      />
      <Route
        path="/"
        element={
          <AuthenticatedPage>
            <HomePage />
          </AuthenticatedPage>
        }
      />
      <Route
        path="/doc"
        element={
          <AuthenticatedPage>
            <DocsPage />
          </AuthenticatedPage>
        }
      />
      <Route
        path="/doc/id/:id/edit"
        element={
          <AuthenticatedPage>
            <DocumentAccessRoute mode="editor" lookup="id" />
          </AuthenticatedPage>
        }
      />
      <Route
        path="/doc/id/:id"
        element={
          <AuthenticatedPage>
            <DocumentAccessRoute mode="reader" lookup="id" />
          </AuthenticatedPage>
        }
      />
      <Route
        path="/doc/:slug/edit"
        element={
          <AuthenticatedPage>
            <DocumentAccessRoute mode="editor" />
          </AuthenticatedPage>
        }
      />
      <Route
        path="/doc/:slug"
        element={
          <AuthenticatedPage>
            <DocumentAccessRoute mode="reader" />
          </AuthenticatedPage>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeSync />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </QueryClientProvider>
  )
}
